const money = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const currencyCode = (value) => {
  const normalized = String(value || "AUD").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "AUD";
};

export const normalizeWalletDetails = (response = {}, fallbackCurrency = "AUD") => {
  const body = response?.data && !Object.prototype.hasOwnProperty.call(response, "balance")
    ? response.data
    : response;
  const balance = money(body?.balance);
  const reserved = money(body?.reserved);
  return {
    wallet_id: Number(body?.wallet_id) || null,
    balance,
    reserved,
    available: body?.available_balance != null
      ? money(body.available_balance)
      : body?.available == null
        ? Math.max(0, balance - reserved)
        : money(body.available),
    currency: currencyCode(body?.currency || fallbackCurrency),
    transactions: (Array.isArray(body?.transactions) ? body.transactions : []).map((row) => ({
      ...row,
      id: Number(row?.id) || null,
      amount: money(row?.amount),
      balance_after: money(row?.balance_after),
    })),
    plans: (Array.isArray(body?.plans) ? body.plans : []).map((row) => ({
      ...row,
      id: Number(row?.id) || null,
      amount: money(row?.amount),
      frequency: String(row?.frequency || row?.repeat_time_option || ""),
      next_billing_at: row?.next_billing_at || row?.next_billing_datetime || null,
      status: String(row?.status || "unknown"),
      failure_message: String(row?.failure_message || row?.invoice_payment_failure_message || ""),
    })),
    next_page_url: body?.next_page_url || null,
  };
};

export const normalizeWalletAmount = (value, wallet, payable) => {
  const requested = Math.max(0, money(value));
  return Math.min(requested, Math.max(0, money(wallet?.available)), Math.max(0, money(payable)));
};

export const walletCheckoutState = ({ requested, available, payable }) => {
  const amount = Math.max(0, money(requested));
  const usable = Math.min(Math.max(0, money(available)), Math.max(0, money(payable)));
  return {
    walletAmount: Math.min(amount, usable),
    remainder: Math.max(0, money(payable) - Math.min(amount, usable)),
    valid: amount <= usable,
  };
};

export const walletRemainder = (payable, walletAmount) => Math.max(
  0,
  money(payable) - Math.max(0, money(walletAmount)),
);

export const walletSalePayload = (values = {}) => {
  const walletAmount = Math.max(0, money(values.wallet_amount));
  if (walletAmount <= 0) return values;
  const existing = Array.isArray(values.payment_allocations)
    ? values.payment_allocations
    : [];
  const remainder = walletRemainder(values.totalPay, walletAmount);
  if (existing.length || remainder <= 0) {
    return { ...values, payment_allocations: existing };
  }
  const paidBy = String(values.paidby || "").trim();
  if (!["BankTransfer", "Cash", "CreditCard"].includes(paidBy)) {
    return { ...values, payment_allocations: existing };
  }
  return {
    ...values,
    payment_allocations: [{ paid_by: paidBy, amount_minor: Math.round(remainder * 100) }],
  };
};

export const formatWalletMoney = (value, currency = "AUD") => `${currencyCode(currency)} ${money(value).toFixed(2)}`;

// The wallet history routes page with a Laravel-style `next_page_url`. The
// details request takes a page number, so a legacy URL must be reduced back to
// its page before it is sent; passing the URL through re-requests page one.
export const walletNextPage = (value) => {
  const direct = Number(value);
  if (Number.isInteger(direct) && direct > 0) return direct;
  if (typeof value !== "string" || !value) return 1;
  try {
    const page = Number(new URL(value, "https://client-wallet.invalid").searchParams.get("page"));
    return Number.isInteger(page) && page > 0 ? page : 1;
  } catch (_error) {
    return 1;
  }
};
